import React, { useCallback, useEffect, useState } from 'react';
import { useAdminAuth } from '../AdminAuthContext';
import { fetchSubscribersAdmin } from '../adminApi';
import { getSubscriberSignedAt } from '../../utils/subscriberFields';

export default function SubscribersPage() {
  const { canEditSubscribers } = useAdminAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const res = await fetchSubscribersAdmin({
      page,
      pageSize,
      search,
      activeOnly
    });
    if (res.error) {
      setError(res.error.message || 'Failed to load');
      setRows([]);
      setTotal(0);
    } else {
      setRows(res.rows);
      setTotal(res.total);
    }
    setLoading(false);
  }, [page, pageSize, search, activeOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-white">Subscribers</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Newsletter emails from the <code className="text-zinc-400">subscribers</code>{' '}
          table (not registered app users).
        </p>
        {!canEditSubscribers && (
          <p className="text-xs text-amber-200/90 mt-2">
            Your role can view this list. Only admins can change records via
            Supabase policies.
          </p>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="search"
          placeholder="Search by email…"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          className="flex-1 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-white"
        />
        <label className="inline-flex items-center gap-2 text-sm text-zinc-400">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => {
              setPage(1);
              setActiveOnly(e.target.checked);
            }}
            className="rounded border-zinc-600"
          />
          Active only
        </label>
      </div>

      {error && <div className="text-sm text-red-400">{error}</div>}

      <div className="rounded-xl border border-zinc-800 overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-zinc-900/80 text-zinc-400 text-xs uppercase">
            <tr>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">Subscribed</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-zinc-500">
                  Loading…
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const signedAt = getSubscriberSignedAt(r);
                return (
                  <tr key={r.id} className="hover:bg-zinc-900/50">
                  <td className="px-3 py-2 text-white">{r.email}</td>
                  <td className="px-3 py-2 text-zinc-400">{r.source || '—'}</td>
                  <td className="px-3 py-2 text-zinc-500">
                    {signedAt ? new Date(signedAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        r.is_active
                          ? 'text-emerald-400 text-xs'
                          : 'text-zinc-500 text-xs'
                      }
                    >
                      {r.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  </tr>
                );
              })
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-zinc-500">
                  No subscribers match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-zinc-500">
        <span>
          Page {page} of {totalPages} · {total.toLocaleString()} total
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded border border-zinc-700 px-3 py-1 disabled:opacity-30"
          >
            Previous
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded border border-zinc-700 px-3 py-1 disabled:opacity-30"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
